import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { currencyFormat } from "./functions";
import Allergens from "./Allergens";
import Tags from "./Tags";
import ProductLabels from "./ProductLabels";
import IconDotsVertical from "./icons/dots";
import Label from "./Label";
import moment from "moment/moment";

export default function Product({ product, category = false }) {
  const {
    settings,
    getTranslatedString,
    cart,
    handleAddProduct,
    setSelectedProduct,
    cartEnabled,
    setCartEnabled,
  } = useContext(AppContext);
  const [discounted, setDiscounted] = useState(false);

  useEffect(() => {
    if (
      product.discounted &&
      (!category ||
        (category.id == -2 && product.discount.one_per_customer == 1) ||
        (category.id != -2 && product.discount.one_per_customer == 0))
    ) {
      setDiscounted(true);
    } else {
      setDiscounted(false);
    }
  }, [product, category]);

  const hasVariants = (product) => {
    if (product.mandatory_notes == 1) return true;
    if (product.variants_categories.length > 0) return true;
    return false;
  };

  const hasMandatoryVariants = (product) => {
    if (product.mandatory_notes == 1) return true;
    for (const vc of product.variants_categories) {
      if (vc.min > 0) return true;
    }
    return false;
  };

  const hasVariants2 = (product) => {
    for (const vc of product.variants_categories) {
      if (vc.variants2.length > 0) return true;
    }
    return false;
  };

  const openProduct = (p) => {
    window.history.replaceState(null, "", `#product-${p.id}`);
    setSelectedProduct({ ...p, discounted });
  };

  const canQuickAdd = !hasMandatoryVariants(product) && !hasVariants2(product);
  const hasAllergens =
    settings.website.show_allergens_in_products_list == 1 &&
    product.allergens &&
    product.allergens.length > 0;
  const hasTags = Array.isArray(product.tags) && product.tags.length > 0;
  const hasCashback =
    cart &&
    cart.shop_id &&
    product.promo &&
    product.promo.cashback &&
    product.promo.cashback[cart.shop_id]?.type &&
    product.promo.cashback[cart.shop_id]?.value > 0;
  const hasLabelsOrTags =
    hasTags ||
    product.vegan == 1 ||
    product.gluten_free == 1 ||
    product.disabled == 1 ||
    discounted == 1 ||
    hasCashback;

  const handleFooterClick = (e) => {
    e.stopPropagation();
    if (product.disabled != 0) return;

    if (hasVariants(product) && !canQuickAdd) {
      if (!cartEnabled) setCartEnabled(true);
      else openProduct(product);
      return;
    }

    handleAddProduct(product, discounted ? 1 : 0);
  };

  const handleOptionsClick = (e) => {
    e.stopPropagation();
    if (!cartEnabled) setCartEnabled(true);
    else openProduct(product);
  };

  return (
    <div className="product-container">
      <div
        className="product"
        role="button"
        tabIndex={0}
        onClick={() => openProduct(product)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openProduct(product);
          }
        }}
      >
        <div className="info">
          <div className="product-title-row">
            <h3>{getTranslatedString(product, "name").replaceAll(",", ", ")}</h3>
          </div>
          {hasLabelsOrTags && (
            <div className="product-title-meta">
              <ProductLabels product={product} discounted={discounted} />
              <Tags product={product} />
            </div>
          )}
          {getTranslatedString(product, "description") && (
            <p
              className="description smoot"
              dangerouslySetInnerHTML={{
                __html: getTranslatedString(product, "description").replaceAll(",", ", "),
              }}
            />
          )}
          {hasAllergens && !product.img && (
            <div className="allergens">
              <Allergens allergens={product.allergens} color="#000" backgroundColor="#ccc" />
            </div>
          )}
          {product.disabled == 0 && (
            <div
              className="product-box-footer"
              role="button"
              tabIndex={0}
              onClick={handleFooterClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleFooterClick(e);
                }
              }}
            >
              {(cartEnabled || settings.website.show_prices_if_no_list_selected == 1) && (
                <div>
                  {discounted ? (
                    <div>
                      <s>{currencyFormat(product?.price, cart?.shop?.currency)}</s>
                      {" - " + currencyFormat(product.discount?.price, cart?.shop?.currency)}
                    </div>
                  ) : (
                    <div>{currencyFormat(product.price, cart?.shop?.currency)}</div>
                  )}
                </div>
              )}
              {!product.img && (
                <div className="product-actions">
                  {hasVariants(product) && canQuickAdd && (
                    <button
                      type="button"
                      className="product-option-button"
                      onClick={handleOptionsClick}
                      aria-label="Opzioni prodotto"
                    >
                      <IconDotsVertical />
                    </button>
                  )}
                  <button
                    type="button"
                    className="product-add-button"
                    onClick={handleFooterClick}
                    aria-label="Aggiungi prodotto"
                  >
                    +
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {product.img && (
          <div className="img">
            <img src={product.img} />
            {product.disabled == 0 && (
              <div className="product-actions product-actions-overlay" onClick={(e) => e.stopPropagation()}>
                {hasVariants(product) && canQuickAdd && (
                  <button
                    type="button"
                    className="product-option-button"
                    onClick={handleOptionsClick}
                    aria-label="Opzioni prodotto"
                  >
                    <IconDotsVertical />
                  </button>
                )}
                <button
                  type="button"
                  className="product-add-button"
                  onClick={handleFooterClick}
                  aria-label="Aggiungi prodotto"
                >
                  +
                </button>
              </div>
            )}
            {hasAllergens && (
              <div className="allergens allergens-hover" onClick={(e) => e.stopPropagation()}>
                <Allergens allergens={product.allergens} color="#000" backgroundColor="#fff" />
              </div>
            )}
          </div>
        )}
      </div>
      {discounted && (product.discount?.date_start || product.discount?.date_end) && (
        <div className="product-discount-dates">
          {product.discount.date_start && (
            <span>
              <Label number={208} /> {moment(product.discount.date_start, "YYYY-MM-DD").format("DD/MM/YYYY")}
            </span>
          )}
          {product.discount.date_end && (
            <span>
              {" "}
              <Label number={209} /> {moment(product.discount.date_end, "YYYY-MM-DD").format("DD/MM/YYYY")}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
